import { useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '../lib/cx';
import { formatDuration } from '../lib/workout';
import { Button } from './Button';
import { ProgressBar } from './ProgressBar';

export interface RestTimerProps {
  seconds: number;
  labels: {
    title: string;
    start: string;
    pause: string;
    resume: string;
    skip: string;
    /** Najava kraja odmora za čitač zaslona; bez nje se najavljuje naslov. */
    done?: string;
  };
  autoStart?: boolean;
  onComplete?: () => void;
  onSkip?: () => void;
  className?: string;
  /** Injektabilno radi determinističnih testova. */
  tickMs?: number;
}

export function RestTimer({
  seconds,
  labels,
  autoStart = false,
  onComplete,
  onSkip,
  className,
  tickMs = 1000,
}: RestTimerProps) {
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(autoStart);
  const doneRef = useRef(false);

  useEffect(() => {
    setRemaining(seconds);
    doneRef.current = false;
  }, [seconds]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setRemaining((r) => (r <= 1 ? 0 : r - 1));
    }, tickMs);
    return () => clearInterval(id);
  }, [running, tickMs]);

  useEffect(() => {
    if (remaining === 0 && running && !doneRef.current) {
      doneRef.current = true;
      setRunning(false);
      onComplete?.();
    }
  }, [remaining, running, onComplete]);

  const skip = useCallback(() => {
    setRunning(false);
    setRemaining(0);
    onSkip?.();
  }, [onSkip]);

  return (
    <div
      className={cx('rounded-xl border border-line-subtle bg-surface p-5 shadow-card', className)}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="eyebrow">{labels.title}</span>
        {/*
          Brojka se mijenja svake sekunde. S `aria-live` je čitač zaslona
          izgovarao svaku od njih i zagušio sve ostalo na ekranu — sada je brojka
          tiha, a najavljuje se samo kraj odmora.
        */}
        <span
          data-testid="rest-timer-remaining"
          className="font-display text-[32px] font-semibold leading-none text-ink tabular-nums"
        >
          {formatDuration(remaining)}
        </span>
      </div>
      <p role="status" className="sr-only">
        {remaining === 0 ? (labels.done ?? labels.title) : ''}
      </p>
      <ProgressBar value={seconds - remaining} max={seconds} label={labels.title} />
      <div className="mt-4 flex gap-2">
        <Button
          size="sm"
          variant={running ? 'secondary' : 'primary'}
          onClick={() => setRunning((v) => !v)}
        >
          {running ? labels.pause : remaining === seconds ? labels.start : labels.resume}
        </Button>
        <Button size="sm" variant="ghost" onClick={skip}>
          {labels.skip}
        </Button>
      </div>
    </div>
  );
}
